import * as React from 'react';
import { getMouseCanvas, GenericChartComponent } from 'react-financial-charts';
import type { MoreProps } from '@/types/commontypes';
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const ChartComponent = GenericChartComponent as unknown as React.ComponentType<Record<string, unknown>>;
export interface InteractiveTextProps {
  readonly bgFillStyle: string;
  readonly bgStrokeWidth: number;
  readonly bgStroke: string;
  readonly defaultClassName?: string;
  readonly fontFamily: string;
  readonly fontSize: number;
  readonly fontWeight: number | string;
  readonly fontStyle: string;
  readonly onDragStart?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDrag?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDragComplete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onUnHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly position?: [number, number];
  readonly interactiveCursorClass?: string;
  readonly selected: boolean;
  readonly text: string;
  readonly textFill: string;
  readonly tolerance: number;
  readonly showBackground: boolean;
  readonly onClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onMouseDown?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly paddingX: number;
  readonly paddingY: number;
  readonly usePixelCoordinates?: boolean;
}

export class InteractiveText extends React.Component<InteractiveTextProps> {
  public static defaultProps = {
    type: 'SD', // standard dev
    fontWeight: 'normal', // standard dev
    tolerance: 4,
    selected: false,
    usePixelCoordinates: false,
  };

  private calculateTextWidth = true;
  private textWidth?: number;

  public componentDidUpdate(previousProps: InteractiveTextProps) {
    this.calculateTextWidth =
      previousProps.text !== this.props.text ||
      previousProps.fontStyle !== this.props.fontStyle ||
      previousProps.fontWeight !== this.props.fontWeight ||
      previousProps.fontSize !== this.props.fontSize ||
      previousProps.fontFamily !== this.props.fontFamily;
  }

  public render() {
    const { selected, interactiveCursorClass } = this.props;
    const { onHover, onUnHover } = this.props;
    const { onDragStart, onDrag, onDragComplete, onDoubleClick } = this.props;

    return (
      <ChartComponent
        isHover={this.isHover}
        canvasToDraw={getMouseCanvas}
        canvasDraw={this.drawOnCanvas}
        interactiveCursorClass={interactiveCursorClass}
        selected={selected}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragComplete={onDragComplete}
        onHover={onHover}
        onUnHover={onUnHover}
        drawOn={['none', 'mousemove', 'mouseleave', 'pan', 'drag']}
        onClick={this.handleClick}
        onMouseDown={this.handleMouseDown}
        onDoubleClick={onDoubleClick}
      />
    );
  }

  private readonly isHover = (moreProps: MoreProps) => {
    const { onHover } = this.props;

    if (onHover !== undefined && this.textWidth !== undefined && !this.calculateTextWidth) {
      const { rect } = this.helper(moreProps, this.textWidth);
      const {
        mouseXY: [x, y],
      } = moreProps;

      if (x >= rect.x && y >= rect.y && x <= rect.x + rect.width && y <= rect.y + rect.height) {
        return true;
      }
    }
    return false;
  };

  private readonly drawOnCanvas = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    const {
      bgFillStyle,
      bgStrokeWidth,
      bgStroke,
      textFill,
      fontFamily,
      fontSize,
      fontStyle,
      fontWeight,
      text,
      showBackground,
      paddingX,
      paddingY,
    } = this.props;

    if (this.calculateTextWidth) {
      ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
      const lines = text.split('\n');
      const lineWidths = lines.map(line => ctx.measureText(line).width);
      this.textWidth = Math.max(...lineWidths); // Use the widest line
      this.calculateTextWidth = false;
    }

    const { selected } = this.props;

    const { x: _x, y: _y, rect } = this.helper(moreProps, this.textWidth ?? 0);

    if (showBackground) {
      ctx.fillStyle = bgFillStyle;

      ctx.beginPath();
      ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
    }

    if (selected) {
      ctx.strokeStyle = bgStroke;
      ctx.lineWidth = bgStrokeWidth;
      ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
    }

    ctx.fillStyle = textFill;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;

    const lines = text.split('\n');
    const lineHeight = fontSize * 1.45;

    // Compute total text block height
    const textBlockHeight = lineHeight * lines.length;

    // Shift x to include padding
    const startX = rect.x + paddingX;

    // Compute the top-left y so that text block is vertically centered
    const startY = rect.y + paddingY + (rect.height - 2 * paddingY - textBlockHeight) / 2 + lineHeight / 2;

    lines.forEach((line, index) => {
      const lineY = startY + index * lineHeight;
      ctx.beginPath();
      ctx.fillText(line, startX, lineY);
    });
  };

  private readonly helper = (moreProps: MoreProps, textWidth: number) => {
    const { position, fontSize, paddingX, paddingY, usePixelCoordinates } = this.props;
    const [xValue, yValue] = position ?? [0, 0];

    let x: number, y: number;

    if (usePixelCoordinates) {
      x = xValue;
      y = yValue;
    } else {
      const {
        xScale,
        chartConfig: { yScale },
      } = moreProps;
      x = xScale(xValue);
      y = yScale(yValue);
    }

    const lines = this.props.text.split('\n');
    const lineHeight = fontSize;
    const height = lineHeight * lines.length;

    const rect = {
      x: x - textWidth / 2 - paddingX,
      y: y - height / 2 - paddingY,
      width: textWidth + paddingX * 2,
      height: height + paddingY * 3,
    };

    return {
      x,
      y,
      rect,
    };
  };
  private readonly handleClick = (e: React.MouseEvent, moreProps: MoreProps) => {
    const isHovered = this.isHover(moreProps);
    if (isHovered && this.props.onClick) {
      this.props.onClick(e, moreProps);
    }
  };
  private readonly handleMouseDown = (e: React.MouseEvent, moreProps: MoreProps) => {
    const isHovered = this.isHover(moreProps);
    if (isHovered && this.props.onMouseDown) {
      this.props.onMouseDown(e, moreProps);
    }
  };
}
